import { useMemo, useCallback, useRef, useState } from 'react';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import {
  components,
  OptionProps,
  GroupBase,
  IndicatorsContainerProps,
  NoticeProps,
  MultiValue,
} from 'react-select';
import type { MultiValueProps } from 'react-select';
import { ChevronDown, Loader, PackageOpen } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import Option from '@/components/form/types/Option.ts';
import { getSelectStyles } from '@/components/form/_primitives/primitiveSelectStyles.ts';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { useMobileSelectMenu } from '@/components/form/_primitives/mobileSelectMenu.ts';

type PrimitiveAsyncMultiSelectProps = {
  value?: string[];
  onChange?: (values: string[]) => void;
  loadOptions: LoadOptions<Option, GroupBase<Option>, { page: number }>;
  error?: string;
  id?: string;
  name?: string;
  pageSize?: number;
  initialOptions?: Option[];
  placeholder?: string;
  isDisabled?: boolean;
  /** Tytuł arkusza z opcjami poniżej `md`; domyślnie placeholder. */
  mobileMenuTitle?: string;
};

const PrimitiveAsyncMultiSelect = ({
  value = [],
  onChange,
  loadOptions,
  error,
  id,
  name,
  pageSize = 20,
  initialOptions = [],
  placeholder = 'Wyszukaj...',
  isDisabled,
  mobileMenuTitle,
}: PrimitiveAsyncMultiSelectProps) => {
  const mobileMenu = useMobileSelectMenu({
    title: mobileMenuTitle ?? placeholder,
    searchable: true,
  });
  const isMobileMenu = mobileMenu !== null;
  const styles = useMemo(
    () => getSelectStyles<true>(error, 'default', { mobileMenu: isMobileMenu }),
    [error, isMobileMenu],
  );
  const loadedOptionsRef = useRef<Option[]>([]);
  const [resolvedInitials, setResolvedInitials] = useState<Option[]>(
    () => initialOptions,
  );

  const wrappedLoadOptions: LoadOptions<
    Option,
    GroupBase<Option>,
    { page: number }
  > = useCallback(
    async (search, loadedOptions, additional) => {
      const result = await loadOptions(search, loadedOptions, additional);

      if (additional?.page === 0) {
        loadedOptionsRef.current = result.options;
      } else {
        loadedOptionsRef.current = [
          ...loadedOptionsRef.current,
          ...result.options,
        ];
      }

      return result;
    },
    [loadOptions],
  );

  const selectedValues = useMemo(() => {
    if (!value.length) return [];

    return value
      .map((v) => {
        const fromLoaded = loadedOptionsRef.current.find(
          (opt) => opt.value === v,
        );
        if (fromLoaded) return fromLoaded;
        const fromInitial = resolvedInitials.find((opt) => opt.value === v);
        if (fromInitial) return fromInitial;
        return { value: v, label: '' } as Option;
      })
      .filter(Boolean);
  }, [value, resolvedInitials]);

  const handleChange = useCallback(
    (selectedOptions: MultiValue<Option>) => {
      const newInitials = [...resolvedInitials];
      selectedOptions.forEach((opt) => {
        if (!newInitials.find((i) => i.value === opt.value)) {
          newInitials.push(opt);
        }
      });
      setResolvedInitials(newInitials);
      onChange?.(selectedOptions.map((opt) => opt.value as string));
    },
    [onChange, resolvedInitials],
  );

  return (
    <AsyncPaginate<Option, GroupBase<Option>, { page: number }, true>
      inputId={id ?? name}
      name={name}
      placeholder={placeholder}
      loadOptions={wrappedLoadOptions}
      defaultOptions
      additional={{ page: 0 }}
      debounceTimeout={300}
      classNamePrefix="custom-select"
      isSearchable
      isMulti
      isDisabled={isDisabled}
      value={selectedValues}
      onChange={handleChange}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldBlockScroll={true}
      cacheUniqs={[]}
      pageSize={pageSize}
      components={{
        Option: CheckboxOption,
        IndicatorsContainer: CustomIndicator,
        NoOptionsMessage: CustomNoOptions,
        MultiValue: CustomMultiValue,
        LoadingIndicator: () => null,
        LoadingMessage: CustomLoadingMessage,
        ...mobileMenu?.components,
      }}
      styles={styles}
      {...mobileMenu?.selectProps}
    />
  );
};

const CustomMultiValue = (
  props: MultiValueProps<Option, true, GroupBase<Option>>,
) => (
  <Chip
    // Wyszukiwarka zwraca etykiety tekstowe; węzeł zdarza się tylko w wyborze
    // pojedynczym.
    label={props.data.label as string}
    fill="soft"
    // react-select woła `removeProps.onClick` bez zdarzenia — to samo usunięcie.
    onRemove={props.removeProps.onClick as (() => void) | undefined}
  />
);

const CheckboxOption = (
  props: OptionProps<Option, true, GroupBase<Option>>,
) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <PrimitiveCheckbox
          checked={props.isSelected}
          readOnly
          tabIndex={-1}
          className="border-neutral-200"
        />
        <label className="cursor-pointer text-sm leading-4">
          {props.label}
        </label>
      </div>
    </components.Option>
  );
};

const CustomIndicator = (props: IndicatorsContainerProps<Option, true>) => {
  const isLoading = props.selectProps.isLoading;
  return (
    <div className="p-1 px-2">
      <components.IndicatorsContainer {...props}>
        <div className="flex items-center gap-1">
          {isLoading && (
            <Loader className="animate-spin text-neutral-900" size={16} />
          )}
          <ChevronDown
            className={cn('text-neutral-600 hover:text-neutral-900')}
            size={20}
            strokeWidth={2}
          />
        </div>
      </components.IndicatorsContainer>
    </div>
  );
};

const CustomNoOptions = (props: NoticeProps<Option, true>) => {
  return (
    <components.NoOptionsMessage {...props}>
      <div className="flex flex-row items-start gap-1 text-sm text-neutral-600">
        <PackageOpen className="size-5 min-w-5 text-neutral-400" />
        <p className="truncate">Brak wyników</p>
      </div>
    </components.NoOptionsMessage>
  );
};

const CustomLoadingMessage = (props: NoticeProps<Option, true>) => {
  return (
    <components.LoadingMessage {...props}>
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: 14 }).map((_, index) => (
          <Skeleton key={index} className="h-5" />
        ))}
      </div>
    </components.LoadingMessage>
  );
};

export default PrimitiveAsyncMultiSelect;
